import { connect } from 'react-redux';
import { UserRole, instructorRoles, panelRoles, programManagerRoles } from '../store/constants/enums';
import * as Types from '../store/types';

// Base permission structure that all modules should follow
export type BasePermission = {
  roles: UserRole[];
  description?: string;
  category?: string; // For grouping related permissions
};

// Helper function to ensure SYSTEM role always has access
const withSystemAccess = (roles: UserRole[]): UserRole[] => {
  // Always include SYSTEM role and filter out duplicates
  return [UserRole.SYSTEM, ...roles.filter((role) => role !== UserRole.SYSTEM)];
};

export const CommonPermissions = {
  // Panel access permissions
  panelAccess: {
    roles: withSystemAccess(panelRoles),
    description: 'Access granted to panel users and system',
    category: 'panel',
  },
  panelAndInstructorAccess: {
    roles: withSystemAccess([...panelRoles, ...instructorRoles]),
    description: 'Access granted to panel, instructors and system',
    category: 'panel',
  },
  panelAndProgramManagerAccess: {
    roles: withSystemAccess([...panelRoles, ...programManagerRoles]),
    description: 'Access granted to panel, program managers and system',
    category: 'panel',
  },

  // Instructor access permissions
  instructorAccess: {
    roles: withSystemAccess(instructorRoles),
    description: 'Access granted to instructors and system',
    category: 'instructor',
  },
  instructorAndProgramManagerAccess: {
    roles: withSystemAccess([...instructorRoles, ...programManagerRoles]),
    description: 'Access granted to instructors, program managers and system',
    category: 'instructor',
  },

  // Program manager access permissions
  programManagerAccess: {
    roles: withSystemAccess(programManagerRoles),
    description: 'Access granted to program managers and system',
    category: 'program-manager',
  },

  // System-only permissions (system already has access to everything, but these are explicitly system-only)
  systemOnly: {
    roles: [UserRole.SYSTEM],
    description: 'Access granted only to system',
    category: 'system',
  },
};

// Helper function to create permissions for all roles except specified ones
export const createPermissionsExcludingRoles = (excludeRoles: UserRole[] = []): BasePermission => {
  // Get all roles from UserRole enum
  const allRoles = Object.values(UserRole).filter((value) => typeof value === 'string') as UserRole[];

  // Filter out excluded roles
  const allowedRoles = allRoles.filter((role) => !excludeRoles.includes(role));

  return {
    roles: allowedRoles,
    description: 'Access granted to all roles except: ' + excludeRoles.join(', '),
    category: 'custom',
  };
};

// Create a hook that modules can use to define their permissions
export const usePermissions = (user?: Types.IAuthUser) => {
  const canAccess = (permission: BasePermission): boolean => {
    if (!user) return false;
    return permission.roles.includes(user.role as UserRole);
  };

  // Helper to check multiple permissions
  const canAccessAny = (permissions: BasePermission[]): boolean => {
    return permissions.some((permission) => canAccess(permission));
  };

  // Helper to check if user has all permissions
  const canAccessAll = (permissions: BasePermission[]): boolean => {
    return permissions.every((permission) => canAccess(permission));
  };

  return { canAccess, canAccessAny, canAccessAll };
};

// Connect the hook to Redux
export const withPermissions = connect((store: Types.IPersistedState) => ({
  user: store.state.user,
}));
